export type ParametersCsvField =
  | "orderName"
  | "orderId"
  | "orderTags"
  | "returnRequestId"
  | "createdAt"
  | "shippedAt"
  | "status"
  | "returnCode"
  | "orderedAt"
  | "updatedAt"
  | "shippingAt"
  | "treatedLogisticianAt"
  | "treatedMerchantAt"
  | "pickupTiming"
  | "pickupTimingEnd"
  | "internalComments"
  | "comment"
  | "customerImages"
  | "returnToStoreExternalId";

export type ShippingAndCustomerFields =
  | "customerPhone"
  | "customerName"
  | "customerEmail"
  | "newVariantSku"
  | "lineItemId"
  | "shippingAddress1"
  | "shippingAddress2"
  | "shippingCity"
  | "shippingZip"
  | "shippingCountry"
  | "shippingCountryCode"
  | "shippingInfoCarrier"
  | "shippingInfoTrackingCode"
  | "customerId"
  | "customerFirstName"
  | "customerLastName"
  | "lang";

export type DataTreatmentCsvField =
  | "logisticianTreatedAt"
  | "merchantTreatedAt"
  | "assignedTo"
  | "logisticianValidated"
  | "logisticianRejected"
  | "logisticianReasonRejected"
  | "merchantValidated"
  | "merchantRejected"
  | "merchantReasonRejected"
  | "variantId"
  | "quantity"
  | "newVariantId"
  | "newVariantBarcode"
  | "newVariantPrice"
  | "newVariantQuantity"
  | "exchangeCreditItems"
  | "locationId"
  | "newOrderName"
  | "externalStoreId";

export type ResultCsvField =
  | "sku"
  | "refunded"
  | "discounted"
  | "voucherCode"
  | "initialShippingRefund"
  | "returnShippingPaid"
  | "returnShippingTaxEstimated"
  | "returnShippingTaxFinal"
  | "returnShippingTaxGap"
  | "lineItemId"
  | "title"
  | "price"
  | "originalPrice"
  | "conversionRateCustomerOnShop"
  | "reasonId"
  | "reason"
  | "reasonExternalId"
  | "productId"
  | "quantityBought"
  | "variantTitle"
  | "variantBarcode"
  | "resolution"
  | "metafieldsVariant"
  | "metafieldsProduct"
  | "metafieldsOrder"
  | "customAttributesRR"
  | "rebuyAmount";

export const parametersFields: ParametersCsvField[] = [
  "orderId",
  "returnRequestId",
  "orderName",
  "orderTags",
  "returnCode",
  "createdAt",
  "orderedAt",
  "updatedAt",
  "shippedAt",
  "shippingAt",
  "treatedLogisticianAt",
  "treatedMerchantAt",
  "pickupTiming",
  "pickupTimingEnd",
  "status",
  "internalComments",
  "comment",
  "customerImages",
  "returnToStoreExternalId",
];

export const shippingAndCustomerFields: ShippingAndCustomerFields[] = [
  "customerPhone",
  "customerName",
  "customerEmail",
  "lineItemId",
  "newVariantSku",
  "shippingAddress1",
  "shippingAddress2",
  "shippingCity",
  "shippingZip",
  "shippingCountry",
  "shippingCountryCode",
  "shippingInfoCarrier",
  "shippingInfoTrackingCode",
  "customerId",
  "customerFirstName",
  "customerLastName",
  "lang",
];

export const dataTreatmentFields: DataTreatmentCsvField[] = [
  "logisticianTreatedAt",
  "merchantTreatedAt",
  "assignedTo",
  "logisticianValidated",
  "logisticianRejected",
  "logisticianReasonRejected",
  "merchantValidated",
  "merchantRejected",
  "merchantReasonRejected",
  "variantId",
  "quantity",
  "newVariantId",
  "newVariantBarcode",
  "newVariantPrice",
  "newVariantQuantity",
  "exchangeCreditItems",
  "locationId",
  "newOrderName",
  "externalStoreId",
];

export const resultFields: ResultCsvField[] = [
  "refunded",
  "discounted",
  "voucherCode",
  "initialShippingRefund",
  "returnShippingPaid",
  "rebuyAmount",
  "lineItemId",
  "sku",
  "title",
  "price",
  "originalPrice",
  "conversionRateCustomerOnShop",
  "reason",
  "reasonId",
  "reasonExternalId",
  "productId",
  "quantityBought",
  "variantTitle",
  "variantBarcode",
  "resolution",
  "metafieldsVariant",
  "metafieldsProduct",
  "metafieldsOrder",
  "customAttributesRR",
];

export type FieldsSections =
  | ParametersCsvField
  | ShippingAndCustomerFields
  | DataTreatmentCsvField
  | ResultCsvField;

export type SectionKeys =
  | "parameters"
  | "shippingCustomer"
  | "treatment"
  | "result";

export interface ExportReturnFieldsSection<T extends FieldsSections> {
  titleKey: SectionKeys;
  fields: T[];
}

export const exportReturnFieldsBySections: ExportReturnFieldsSection<FieldsSections>[] =
  [
    { titleKey: "parameters", fields: parametersFields },
    {
      titleKey: "shippingCustomer",
      fields: shippingAndCustomerFields,
    },
    {
      titleKey: "treatment",
      fields: dataTreatmentFields,
    },
    {
      titleKey: "result",
      fields: resultFields,
    },
  ];

/**
 * BBS-366: Shopify tax on the return fee, only offered to a shop charging it
 * (super admin option): other merchants keep the exact same list.
 */
const returnFeeTaxResultFields: ResultCsvField[] = [
  "returnShippingTaxEstimated",
  "returnShippingTaxFinal",
  "returnShippingTaxGap",
];

export const getExportReturnFieldsBySections = (
  hasShopifyTaxOnUsReturnFee: boolean,
): ExportReturnFieldsSection<FieldsSections>[] =>
  hasShopifyTaxOnUsReturnFee
    ? exportReturnFieldsBySections.map((section) =>
        section.titleKey === "result"
          ? {
              ...section,
              fields: [...section.fields, ...returnFeeTaxResultFields],
            }
          : section,
      )
    : exportReturnFieldsBySections;

export const exportReturnFields: Array<
  | ParametersCsvField
  | ShippingAndCustomerFields
  | DataTreatmentCsvField
  | ResultCsvField
> = [
  ...parametersFields,
  ...shippingAndCustomerFields,
  ...dataTreatmentFields,
  ...resultFields,
];

export type ExportReturnFieldsBySections = {
  [key in SectionKeys]: FieldsSections[];
};

/**
 * Fields checked when the export dialog opens, derived from the visible
 * columns of the returns table: the `customer` and `order` columns check
 * every field whose name contains them.
 */
export const getDefaultExportReturnFields = (
  fieldsAlreadySelected: string[],
): ExportReturnFieldsBySections => {
  const fieldsBySections = exportReturnFieldsBySections.map((section) => {
    const fields = section.fields.filter((field) => {
      if (field.includes("customer"))
        return fieldsAlreadySelected.includes("customer");
      // Opt-in only: preselecting it would insert a column into every
      // merchant's default export and shift the following ones.
      if (field === "orderTags") return false;
      if (field.includes("order"))
        return fieldsAlreadySelected.includes("order");
      return fieldsAlreadySelected.includes(field);
    });
    return { [section.titleKey]: fields };
  });
  return Object.assign({}, ...fieldsBySections);
};
